/*
 * Keyboard shortcuts — recruiter-app-spec §20's "keyboard nav (`/` `c` `[`, arrows/enter in lists)"
 * and §26's "recruiters live on keys".
 *
 * **A registry, not a key map.** The obvious implementation is a `keydown` listener in the shell
 * holding a switch over `/`, `c` and `[`. It falls apart on the second screen: `c` means "add a
 * client" on the client list and "add a candidate" on the candidate list, and the shell would have
 * to know which screen it was showing to decide — putting screen knowledge in the frame, which is
 * the one thing the frame is supposed to be free of.
 *
 * So a screen *registers* what it can do under a name (`search`, `create`), the shell owns the
 * binding from key to name, and neither knows the other's half.
 *
 * ── There is no `?` card, and that is a decision — 31 Aug 2026 ────────────────────────────────
 *
 * There was one: `?` opened a dialog listing what the current screen had registered, and it was
 * **the one shortcut that deliberately fired from inside a field**, on the reasoning that somebody
 * who cannot remember the shortcuts is often mid-form when they realise it.
 *
 * That reasoning was wrong in the only way that matters: `?` is a character people type. The
 * copilot's ask box is a question box, and every question that ended in a question mark opened a
 * dialog over the half-written question instead of punctuating it — a shortcut layer breaking the
 * primary input of the feature it sits inside. **A help card is worth less than the ability to type
 * a question mark**, so the card is gone and the key is text again everywhere.
 *
 * Nothing replaces it, on purpose. The three remaining bindings are `/`, `c` and `[`, they are in
 * the spec, and a card listing three keys was never what made them discoverable.
 *
 * **Three refusals, each of which is a bug somebody has shipped:**
 *
 *   1. Not while focus is in a field. Typing "create" into a note must not fire `c` on the "c".
 *      Checked by element rather than by a flag, because a flag is something a screen can forget to
 *      clear — and the failure mode is a recruiter's typing silently triggering navigation.
 *   2. Not with a modifier held. `Ctrl+C` is copy and `Cmd+/` is the browser's; a shortcut layer
 *      that swallowed either would be actively hostile.
 *   3. Not when the key is a repeat. Holding a key down should not fire an action forty times.
 */

import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';

/**
 * The actions a screen can offer.
 *
 * A fixed union rather than free strings: the shell binds a key to each of these, and a screen that
 * registered `"serch"` would get a shortcut nobody could press and no error to say so.
 */
export type ShortcutAction = 'search' | 'create' | 'selectAll' | 'clearSelection' | 'copilot';

/** Key → action. The shell's half of the contract, and the only place a literal key appears. */
const BINDINGS: Readonly<Record<string, ShortcutAction>> = {
  '/': 'search',
  c: 'create',
  a: 'selectAll',
  Escape: 'clearSelection',
  /*
   * §20's third key, and the shell's rather than a screen's.
   *
   * Registered by `RootLayout` through the same `useShortcut` every screen uses, so the frame goes
   * through the registry rather than keeping a private listener beside it — one path from key to
   * handler, and no second place for a binding to hide.
   */
  '[': 'copilot',
};

type Register = (action: ShortcutAction, handler: () => void) => () => void;

/*
 * **One context, holding a value that never changes.**
 *
 * There were two. The set of *available* actions lived in its own context because the help overlay
 * re-rendered on it, and it had to be separate: `available` changes every time a screen registers,
 * so a combined value changed, so every `useShortcut` effect re-ran, so every screen unregistered
 * and re-registered — three registrations and two removals for a single mount, observed in a
 * browser console. It settled, but only by luck of ordering, and each pass leaves a window with no
 * handler in the map where a keystroke does nothing.
 *
 * The overlay is gone (see the header), and with it the only reader that needed to know what was
 * available. So the second context went with it rather than being left as a published value nothing
 * subscribes to. The register function still lives in a context whose value never changes, which is
 * what makes a screen register exactly once, on mount.
 */
const RegisterContext = createContext<Register | null>(null);

/** True when a keystroke belongs to whatever the person is typing into. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export function ShortcutProvider({ children }: { children: ReactNode }) {
  /*
   * A ref, not state.
   *
   * `register` is called from an effect on every screen that offers a shortcut, and a state update
   * there would re-render every one of them on mount — including the screen doing the registering,
   * which then registers again. The listener reads the current map when a key is pressed, which is
   * the only moment its contents matter.
   */
  const handlers = useRef(new Map<ShortcutAction, () => void>());

  const register = useCallback<Register>((action, handler) => {
    handlers.current.set(action, handler);

    return () => {
      // Guarded: a screen unmounting *after* its replacement registered the same action would
      // otherwise delete the new screen's handler on the way out.
      if (handlers.current.get(action) === handler) {
        handlers.current.delete(action);
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      // Escape reaches a screen even from a field: dismissing is what Escape means everywhere, and
      // a selection you cannot clear without reaching for the mouse is worse than no shortcut.
      //
      // Every other key, `?` now included, is text while somebody is typing. There is no longer any
      // exception to this rule, which is the whole of what removing the help card changed.
      const typing = isTyping(event.target);
      if (typing && event.key !== 'Escape') {
        return;
      }

      const action = BINDINGS[event.key];
      const handler = action ? handlers.current.get(action) : undefined;
      if (!handler) {
        return;
      }

      // Only once a handler is found. Calling `preventDefault` before the lookup would swallow "/"
      // on every screen that does not have a search field, including the browser's own quick-find.
      event.preventDefault();
      handler();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // Nothing in the closure changes: `handlers` is a ref and the bindings are module-level. The
    // listener is attached once for the life of the app, which is what it was always meant to be —
    // the old `[helpOpen]` re-attached it on every open and close.
  }, []);

  return <RegisterContext.Provider value={register}>{children}</RegisterContext.Provider>;
}

/**
 * Offers one shortcut for as long as the calling screen is mounted.
 *
 * The handler is held in a ref so that a screen passing an inline closure — which is every screen —
 * does not re-register on every render. Registration is about *which screen is showing*; the
 * closure it happens to hold this render is not that.
 */
export function useShortcut(action: ShortcutAction, handler: () => void): void {
  const register = useContext(RegisterContext);
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    if (!register) {
      return;
    }
    return register(action, () => latest.current());
  }, [register, action]);
}

/**
 * Focuses an element from a shortcut — the `/` case, which every list screen wants.
 *
 * `select()` as well as `focus()`: a recruiter pressing `/` with a filter already typed means "let
 * me type a different one", and leaving the caret at the end makes them clear it by hand.
 */
export function useFocusShortcut(
  action: ShortcutAction,
  ref: RefObject<HTMLInputElement | null>,
): void {
  useShortcut(action, () => {
    ref.current?.focus();
    ref.current?.select();
  });
}
